import { cn } from "@/utils/cn"
import { useEffect, useRef } from "react"

/**
 * Applies a mask on the element based on --split-x CSS variable from parent
 * @usage add data-[inverted=true]:classname to apply styles to the inverted version
 * @usage set --split-x CSS variable on parent to control split position
 */
export function Split({
  disabled,
  children,
  className,
  globalClassName,
}: {
  disabled?: boolean
  children?: React.ReactNode
  className?: string
  globalClassName?: string
}) {
  const elementRef = useRef<HTMLDivElement>(null)
  const maskRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!elementRef.current || !maskRef.current) return

    const updateRelativePosition = () => {
      if (!elementRef.current || !maskRef.current) return
      const rect = elementRef.current.getBoundingClientRect()
      maskRef.current.style.setProperty("--relative-split-x", `${rect.left}px`)
    }

    updateRelativePosition()

    window.addEventListener("resize", updateRelativePosition)
    window.addEventListener("scroll", updateRelativePosition)

    const resizeObserver = new ResizeObserver(updateRelativePosition)
    resizeObserver.observe(elementRef.current)

    return () => {
      window.removeEventListener("resize", updateRelativePosition)
      window.removeEventListener("scroll", updateRelativePosition)
      resizeObserver.disconnect()
    }
  }, [disabled])

  return (
    <div ref={elementRef} className={cn("relative", globalClassName)}>
      <div className={className}>{children}</div>
      {/* Inverted element  */}
      {!disabled && (
        <div
          ref={maskRef}
          className="pointer-events-none absolute inset-0 z-10"
          style={
            {
              "--relative-split-x": "0px",
              transform: "translateZ(0)",
              willChange: "mask-image",
              maskImage:
                "linear-gradient(90deg, black calc(max(0px, var(--split-x) - var(--relative-split-x) + 1px)), transparent calc(max(0px, var(--split-x) - var(--relative-split-x))))",
              WebkitMaskImage:
                "linear-gradient(90deg, black calc(max(0px, var(--split-x) - var(--relative-split-x) + 1px)), transparent calc(max(0px, var(--split-x) - var(--relative-split-x))))",
            } as React.CSSProperties
          }
        >
          <div className={className} data-inverted="true">
            {children}
          </div>
        </div>
      )}
    </div>
  )
}
